import React from "react";

import { FormProvider, useForm } from "react-hook-form";

import { Button, FormButton } from "@components/buttons";
import SearchInputField from "@components/inputs/shared/SearchInputField";
import { useRootAppStore } from "@hooks";
import { MobilePlatform } from "@state/stores/types";
import { isCapacitorPlatform } from "@utils/specific/capacitor/helpers";
import useSafeAreaListener from "@utils/specific/capacitor/useSafeAreaListener";
import { HEADER_HEIGHT } from "@utils/style/constants";
import { observer } from "mobx-react";

import CasinoSearchErrorPopup from "./CasinoSearchErrorPopup";

export default observer(function CasinoHeaderSearch() {
	const rootStore = useRootAppStore<MobilePlatform>();

	const casinoSearchStore = rootStore.casinoSearchStore;

	const methods = useForm({
		mode: "onChange",
		defaultValues: {
			casinoSearch: casinoSearchStore.searchTerm,
		},
	});

	const { top } = useSafeAreaListener();

	const handleKeyUp: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
		const code = e.which;

		if (code === 13) {
			if (!casinoSearchStore.isSearchLongEnough) {
				return;
			}

			if (
				!casinoSearchStore.searchValidation.test(
					casinoSearchStore.searchTerm
				)
			) {
				return;
			}
			casinoSearchStore.onSubmit();
			methods.reset();
		}

		if (code === 27) {
			casinoSearchStore.resetSearch();
			methods.reset();
		}
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		casinoSearchStore.onSearchTermChange(e.target.value);
	};

	const handleClose = () => {
		casinoSearchStore.resetSearch();
		methods.reset();
	};

	if (!casinoSearchStore.isSearchOpen) {
		return null;
	}

	const searchOverlayPadding = isCapacitorPlatform()
		? { top: top + HEADER_HEIGHT }
		: {};

	return (
		<FormProvider {...methods}>
			<div
				className={
					"header__main header--root__main" +
					(casinoSearchStore.isSearchOpen
						? " u-padd--left--reset"
						: "")
				}
			>
				<Button
					onClick={handleClose}
					atClassName="headerBtn--Search-Close"
					className="header--page__back btn btn--square btn--ghost"
					iconOnly
					iconClassName="u-icon--tny u-icon--caret-left u-color--core--snow"
				/>
				<div className="header__content header__searchbar">
					<SearchInputField
						name="casinoSearch"
						type="search"
						validate={(value: string | undefined) => {
							if (value == null) {
								return "";
							}
							if (
								value.trim().length > 0 &&
								value.trim().length < 3
							) {
								casinoSearchStore.setIsErrorOpen(true);
								return "SEARCH.INFO";
							}

							if (
								value.trim().length > 0 &&
								!casinoSearchStore.searchValidation.test(value)
							) {
								casinoSearchStore.setIsErrorOpen(true);
								return "SEARCH.INFO";
							}

							casinoSearchStore.setIsErrorOpen(false);
						}}
						onKeyUp={handleKeyUp}
						onChange={handleChange}
						defaultValue={methods.getValues("casinoSearch") || ""}
						placeholder="HEADER.SEARCH_PLACEHOLDER"
						autoComplete="off"
						autoFocus
						inputClassName="at-searchModalInput header__searchbar__input input input--med"
						customErrorClassName="searchbar__validation message--validation field-validation-error"
					/>
					{casinoSearchStore.isSearchOpen && (
						<div
							onClick={() => {
								handleClose();
							}}
							style={searchOverlayPadding}
							className="header__searchbar__overlay"
						></div>
					)}
				</div>

				<div className="header__actions">
					<FormButton
						onClick={() => {
							casinoSearchStore.onSubmit();
							methods.reset();
						}}
						atClassName="headerBtn--Search-Submit"
						className="btn--square btn--square--sml btn--ghost"
						iconOnly
						iconClassName="u-icon--sml u-icon--search--reg u-color--core--snow"
					/>
				</div>
			</div>
			{casinoSearchStore.isErrorOpen && <CasinoSearchErrorPopup />}
		</FormProvider>
	);
});
